import React, { useState, useEffect } from 'react';
import { portalAPI } from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { Link } from 'react-router-dom';

const PortalManager = () => {
    const [portals, setPortals] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [formData, setFormData] = useState({ title: '', description: '', link: '', order: 0, features: '' });

    const loadPortals = () => {
        portalAPI.getAll().then(res => setPortals(res.data || [])).catch(() => toast.error('Failed to load portals'));
    };

    useEffect(() => { loadPortals(); }, []);

    const resetForm = () => {
        setEditingId(null);
        setFormData({ title: '', description: '', link: '', order: 0, features: '' });
        setImageFile(null);
        setShowForm(false);
    };

    const handleEdit = (portal) => {
        setEditingId(portal.id);
        setFormData({ title: portal.title || '', description: portal.description || '', link: portal.link || '', order: portal.order || 0, features: (portal.features || []).join(', ') });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this portal?')) return;
        try {
            await portalAPI.delete(id);
            toast.success('Portal deleted');
            loadPortals();
        } catch (err) {
            toast.error('Failed to delete portal');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        // client-side validation
        const validate = () => {
            if (!formData.title || formData.title.trim().length < 2) return 'Title is required (min 2 chars)';
            if (formData.link && formData.link.trim().length) {
                try { new URL(formData.link); } catch (err) { return 'Link must be a valid absolute URL (include https://)'; }
            }
            if (isNaN(Number(formData.order)) || Number(formData.order) < 0) return 'Order must be a non-negative number';
            if (imageFile) {
                if (!imageFile.type.startsWith('image/')) return 'Uploaded file must be an image';
                if (imageFile.size > 5 * 1024 * 1024) return 'Image must be smaller than 5MB';
            }
            return null;
        };
        const validationError = validate();
        if (validationError) {
            toast.error(validationError);
            setLoading(false);
            return;
        }
        try {
            const payload = new FormData();
            payload.append('title', formData.title);
            payload.append('description', formData.description);
            payload.append('link', formData.link);
            payload.append('order', formData.order);
            payload.append('features', formData.features);
            if (imageFile) payload.append('image', imageFile);

            if (editingId) {
                await portalAPI.update(editingId, payload);
                toast.success('Portal updated');
            } else {
                await portalAPI.create(payload);
                toast.success('Portal created');
            }
            resetForm();
            loadPortals();
        } catch (err) {
            toast.error('Operation failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6">
            <Toaster />
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Portal Card Management</h1>
                <button onClick={() => setShowForm(!showForm)} className={`px-4 py-2 rounded-md font-semibold text-white ${showForm ? 'bg-red-500' : 'bg-orange-600'}`}>
                    {showForm ? 'Cancel' : '+ Add New Portal'}
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input required placeholder="Title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="p-2 border rounded" />
                    <input placeholder="Link" value={formData.link} onChange={(e) => setFormData({...formData, link: e.target.value})} className="p-2 border rounded" />
                    <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="p-2 border rounded md:col-span-2" />
                    <input type="text" placeholder="Features (comma separated)" value={formData.features} onChange={(e) => setFormData({...formData, features: e.target.value})} className="p-2 border rounded" />
                    <input type="number" placeholder="Order" value={formData.order} onChange={(e) => setFormData({...formData, order: Number(e.target.value)})} className="p-2 border rounded" />
                    <input type="file" onChange={(e) => setImageFile(e.target.files[0])} className="p-2" />
                    <div className="md:col-span-2 flex gap-2">
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded">{loading ? 'Saving...' : (editingId ? 'Update Portal' : 'Create Portal')}</button>
                        <button type="button" onClick={resetForm} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                    </div>
                </form>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {portals.map(p => (
                    <div key={p.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                        {p.imageUrl && <img src={`${process.env.REACT_APP_API_URL}${p.imageUrl}`} alt={p.title} className="w-full h-48 object-cover" />}
                        <div className="p-4">
                            <h3 className="text-lg font-bold">{p.title}</h3>
                            <p className="text-sm text-gray-600 mb-2">{p.description}</p>
                            <div className="flex gap-2 mb-2">{(p.features || []).map((f,i) => <span key={i} className="text-xs px-2 py-1 bg-orange-50 rounded">{f}</span>)}</div>
                            <div className="flex gap-2">
                                <button onClick={() => handleEdit(p)} className="px-3 py-1 bg-blue-600 text-white rounded">Edit</button>
                                <button onClick={() => handleDelete(p.id)} className="px-3 py-1 bg-red-600 text-white rounded">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
export default PortalManager;